/**
 * Supported i18n locales
 */
export enum SupportedLocales {
  EnCA = 'EN_CA',
  FrCA = 'FR_CA',
  EnUS = 'EN_US',
}

/**
 * Array of supported BCP 47 locales
 *
 * Used as the source of truth for locale validation and iteration
 */
export const SUPPORTED_BCP47_LOCALES = ['en-CA', 'en-US', 'fr-CA'] as const

/**
 * Union type of supported BCP 47 language tags
 *
 * Derived from {@link SUPPORTED_BCP47_LOCALES}
 * See {@link https://tools.ietf.org/html/bcp47 | BCP 47 Language Tags}
 */
export type SupportedBCP47Locale = (typeof SUPPORTED_BCP47_LOCALES)[number]

/**
 * Filename format for locale translation files
 *
 * Template literal combining BCP 47 locale with `.json` extension
 * Example: `en-CA.json`, `fr-CA.json`, `en-US.json`
 */
export type SupportedLocaleFilename = `${SupportedBCP47Locale}.json`

/**
 * Mapping of BCP 47 locales to their corresponding translation filename
 *
 * Provides explicit locale-to-filename mapping, ensuring consistency
 * and removing order-dependent assumptions
 */
export const SUPPORTED_LOCALE_FILENAMES_BY_LOCALE = {
  'en-CA': 'en-CA.json',
  'en-US': 'en-US.json',
  'fr-CA': 'fr-CA.json',
} as const satisfies Record<SupportedBCP47Locale, SupportedLocaleFilename>

/**
 * Array of all supported locale filenames
 *
 * Derived from {@link SUPPORTED_BCP47_LOCALES} using the mapping in
 * {@link SUPPORTED_LOCALE_FILENAMES_BY_LOCALE}
 * Use this for iteration and validation of translation files
 */
export const SUPPORTED_LOCALE_FILENAMES: readonly SupportedLocaleFilename[] = SUPPORTED_BCP47_LOCALES.map(
  (locale) => SUPPORTED_LOCALE_FILENAMES_BY_LOCALE[locale]
)
